import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { clientsApi } from '../services/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import { ArrowLeft, Loader2, Plus, X, User, Pencil, Trash2 } from 'lucide-react';
import { LogoHome } from '../components/LogoHome';
import { useConfirm } from '../components/ConfirmProvider';

const LOGO_URL = "https://customer-assets.emergentagent.com/job_e3b3c70d-c72e-49dc-b41a-da6affdc6e46/artifacts/4w4rfom9_IMG_0777.PNG";

export default function ClientsPage() {
  const confirm = useConfirm();
  const { id } = useParams();
  const navigate = useNavigate();
  const { isAdmin, user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [clients, setClients] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [deletingId, setDeletingId] = useState(null);
  
  // Form state
  const [name, setName] = useState('');
  const [ndisNumber, setNdisNumber] = useState('');
  const [dateOfBirth, setDateOfBirth] = useState('');
  const [culturalBackground, setCulturalBackground] = useState('');
  const [preferredLanguage, setPreferredLanguage] = useState('English');
  const [supportNeeds, setSupportNeeds] = useState(['']);
  const [goals, setGoals] = useState(['']);
  const [notes, setNotes] = useState('');

  useEffect(() => {
    loadClients();
  }, []);

  useEffect(() => {
    if (id === 'new') {
      resetForm();
      setShowForm(true);
    } else if (id) {
      loadClientDetails(id);
    }
  }, [id]);

  const loadClients = async () => {
    try {
      const response = await clientsApi.getAll();
      setClients(response.data);
    } catch (error) {
      toast.error('Failed to load clients');
    } finally {
      setLoading(false);
    }
  };

  const loadClientDetails = async (clientId) => {
    try {
      const response = await clientsApi.getById(clientId);
      const client = response.data;
      setName(client.name);
      setNdisNumber(client.ndis_number);
      setDateOfBirth(client.date_of_birth);
      setCulturalBackground(client.cultural_background || '');
      setPreferredLanguage(client.preferred_language || 'English');
      setSupportNeeds(client.support_needs?.length ? client.support_needs : ['']);
      setGoals(client.goals?.length ? client.goals : ['']);
      setNotes(client.notes || '');
      setShowForm(true);
    } catch (error) {
      toast.error('Failed to load client details');
    }
  };

  const resetForm = () => {
    setName('');
    setNdisNumber('');
    setDateOfBirth('');
    setCulturalBackground('');
    setPreferredLanguage('English');
    setSupportNeeds(['']);
    setGoals(['']);
    setNotes('');
  };

  const handleArrayItemChange = (setter, index, value) => {
    setter(prev => {
      const newArr = [...prev];
      newArr[index] = value;
      return newArr;
    });
  };

  const addArrayItem = (setter) => {
    setter(prev => [...prev, '']);
  };

  const removeArrayItem = (setter, index) => {
    setter(prev => prev.filter((_, i) => i !== index));
  };

  const handleSubmit = async () => {
    if (!name || !ndisNumber || !dateOfBirth) {
      toast.error('Please fill in required fields');
      return;
    }

    setSaving(true);
    try {
      const clientData = {
        name,
        ndis_number: ndisNumber,
        date_of_birth: dateOfBirth,
        cultural_background: culturalBackground || null,
        preferred_language: preferredLanguage,
        support_needs: supportNeeds.filter(s => s.trim()),
        goals: goals.filter(g => g.trim()),
        notes: notes || null
      };

      if (id && id !== 'new') {
        await clientsApi.update(id, clientData);
        toast.success('Client updated successfully');
      } else {
        await clientsApi.create(clientData);
        toast.success('Client created successfully');
      }
      setShowForm(false);
      navigate('/clients');
      loadClients();
    } catch (error) {
      toast.error('Failed to save client');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (clientId, clientName, e) => {
    e.stopPropagation();
    if (!(await confirm({ title: `Delete ${clientName}?`, description: 'This cannot be undone.', confirmText: 'Delete participant', destructive: true }))) return;
    
    setDeletingId(clientId);
    try {
      await clientsApi.delete(clientId);
      toast.success(`${clientName} has been removed`);
      setClients(prev => prev.filter(c => c.id !== clientId));
    } catch (error) {
      toast.error('Failed to delete client');
    } finally {
      setDeletingId(null);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-teal-600"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50" data-testid="clients-page">
      <header className="bg-white border-b border-slate-200 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center gap-4">
              <Button variant="ghost" size="sm" onClick={() => navigate(-1)} data-testid="back-btn">
                <ArrowLeft className="h-4 w-4 mr-2" />
                Back
              </Button>
              <div className="h-6 w-px bg-slate-200" />
              <h1 className="font-heading font-semibold text-slate-900">Clients</h1>
            </div>
            <LogoHome className="h-12 w-auto" />
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {showForm ? (
          <Card className="max-w-2xl mx-auto">
            <CardHeader>
              <CardTitle className="font-heading">{id && id !== 'new' ? 'Edit Client' : 'New Client'}</CardTitle>
              <CardDescription>Enter the participant's details</CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Full Name <span className="text-red-500">*</span></Label>
                  <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="John Smith" className="h-12" data-testid="client-name" />
                </div>
                <div className="space-y-2">
                  <Label>NDIS Number <span className="text-red-500">*</span></Label>
                  <Input value={ndisNumber} onChange={(e) => setNdisNumber(e.target.value)} placeholder="431234567" className="h-12" data-testid="client-ndis" />
                </div>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Date of Birth <span className="text-red-500">*</span></Label>
                  <Input type="date" value={dateOfBirth} onChange={(e) => setDateOfBirth(e.target.value)} className="h-12" data-testid="client-dob" />
                </div>
                <div className="space-y-2">
                  <Label>Preferred Language</Label>
                  <Input value={preferredLanguage} onChange={(e) => setPreferredLanguage(e.target.value)} placeholder="English" className="h-12" data-testid="client-language" />
                </div>
              </div>
              <div className="space-y-2">
                <Label>Cultural Background</Label>
                <Input value={culturalBackground} onChange={(e) => setCulturalBackground(e.target.value)} placeholder="e.g., Aboriginal Australian, Vietnamese" className="h-12" data-testid="client-cultural" />
              </div>

              <div className="space-y-3">
                <Label>Support Needs</Label>
                {supportNeeds.map((need, index) => (
                  <div key={index} className="flex gap-2">
                    <Input placeholder={`Support need ${index + 1}`} value={need} onChange={(e) => handleArrayItemChange(setSupportNeeds, index, e.target.value)} className="h-12" />
                    {supportNeeds.length > 1 && (
                      <Button type="button" variant="ghost" size="icon" onClick={() => removeArrayItem(setSupportNeeds, index)}><X className="h-4 w-4" /></Button>
                    )}
                  </div>
                ))}
                <Button type="button" variant="outline" size="sm" onClick={() => addArrayItem(setSupportNeeds)}>
                  <Plus className="h-4 w-4 mr-2" /> Add Support Need
                </Button>
              </div>

              <div className="space-y-3">
                <Label>NDIS Goals</Label>
                {goals.map((goal, index) => (
                  <div key={index} className="flex gap-2">
                    <Input placeholder={`Goal ${index + 1}`} value={goal} onChange={(e) => handleArrayItemChange(setGoals, index, e.target.value)} className="h-12" />
                    {goals.length > 1 && (
                      <Button type="button" variant="ghost" size="icon" onClick={() => removeArrayItem(setGoals, index)}><X className="h-4 w-4" /></Button>
                    )}
                  </div>
                ))}
                <Button type="button" variant="outline" size="sm" onClick={() => addArrayItem(setGoals)}>
                  <Plus className="h-4 w-4 mr-2" /> Add Goal
                </Button>
              </div>

              <div className="space-y-2">
                <Label>Additional Notes</Label>
                <Textarea value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Any additional information..." rows={4} data-testid="client-notes" />
              </div>

              <div className="flex gap-3">
                <Button variant="outline" onClick={() => { setShowForm(false); navigate('/clients'); }} className="flex-1">Cancel</Button>
                <Button onClick={handleSubmit} disabled={saving} className="flex-1 bg-teal-600 hover:bg-teal-700" data-testid="save-client-btn">
                  {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
                  Save Client
                </Button>
              </div>
            </CardContent>
          </Card>
        ) : (
          <div>
            <div className="flex justify-between items-center mb-6">
              <div>
                <h2 className="font-heading text-2xl font-bold text-slate-900">All Participants</h2>
                <p className="text-slate-500">{clients.length} NDIS participant{clients.length !== 1 ? 's' : ''}</p>
              </div>
              {user?.role !== 'worker' && (
                <Button onClick={() => navigate('/clients/new')} className="bg-teal-600 hover:bg-teal-700" data-testid="add-client-btn">
                  <Plus className="h-4 w-4 mr-2" /> Add Client
                </Button>
              )}
            </div>

            {clients.length === 0 ? (
              <Card>
                <CardContent className="py-12 text-center">
                  <User className="h-12 w-12 mx-auto mb-3 text-slate-300" />
                  {user?.role === 'worker' ? (
                    <div data-testid="worker-clients-empty">
                      <p className="text-slate-700 font-medium mb-1">No participants yet</p>
                      <p className="text-slate-500 text-sm max-w-md mx-auto">
                        Your organisation hasn't added any participants. Ask your admin or supervisor to add them under <strong>Clients</strong>.
                      </p>
                    </div>
                  ) : (
                    <>
                      <p className="text-slate-500 mb-4">No clients yet. Add your first client.</p>
                      <Button onClick={() => navigate('/clients/new')} className="bg-teal-600 hover:bg-teal-700">
                        <Plus className="h-4 w-4 mr-2" /> Add Client
                      </Button>
                    </>
                  )}
                </CardContent>
              </Card>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {clients.map((client) => (
                  <Card key={client.id} className="card-hover group relative" data-testid={`client-card-${client.id}`}>
                    <CardContent className="p-6">
                      <div className="flex items-start justify-between mb-4">
                        <div className="h-12 w-12 rounded-full bg-teal-100 flex items-center justify-center">
                          <span className="text-teal-700 font-semibold text-lg">{client.name.charAt(0)}</span>
                        </div>
                        <div className="flex items-center gap-1">
                          {client.cultural_background && (
                            <Badge variant="secondary" className="text-xs mr-1">{client.cultural_background}</Badge>
                          )}
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-8 w-8 text-slate-400 hover:text-teal-600"
                            onClick={() => navigate(`/clients/${client.id}`)}
                            data-testid={`edit-client-${client.id}`}
                          >
                            <Pencil className="h-4 w-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-8 w-8 text-slate-400 hover:text-red-600"
                            onClick={(e) => handleDelete(client.id, client.name, e)}
                            disabled={deletingId === client.id}
                            data-testid={`delete-client-${client.id}`}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </div>
                      <h3 className="font-heading font-semibold text-slate-900 mb-1">{client.name}</h3>
                      <p className="text-sm text-slate-500 font-mono">{client.ndis_number}</p>
                      {client.support_needs?.length > 0 && (
                        <p className="text-xs text-slate-400 mt-2">{client.support_needs.length} support need(s)</p>
                      )}
                      {client.goals?.length > 0 && (
                        <p className="text-xs text-slate-400">{client.goals.length} goal(s)</p>
                      )}
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </div>
        )}
      </main>
    </div>
  );
}
